import { Routes, Route, Link, useNavigate } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { CustomNavigationClient } from "./utils/NavigationClient";
import { loginRequest } from "./authConfig";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import NavListAuth from "./components/Navigation/NavListAuth";
import { routes } from "./components/Routes/RouteHandler";
import { Helmet } from "react-helmet";
import Footer from "./components/Navigation/Footer";
import { useEffect, useState } from "react";
import MfaHandler from "./components/Routes/MfaHandler";
import RemoteRedirect from "./components/Navigation/RemoteRedirect";
import { selectUser } from "./features/userSlice";
import { selectRoles } from "./features/roleSlice";
import { useSelector } from "react-redux";
import { hasRouteAccess } from "./utilities";
import { isSessionSynced, startLiveSession } from "./utils/liveSession";
import LockRoundedIcon from "@mui/icons-material/LockRounded";

// Routes declare `roles`, but historically that array only filtered nav
// menus — any signed-in user could open any URL directly. This guard makes
// the declaration real. It reads the impersonation-aware selectors, so an
// admin previewing another user is bounced exactly like that user would be.
// Page Access grants (/admin/access) let extra roles or people in by path.
function RequireRoles({ roles, path, children }) {
  const user = useSelector(selectUser);
  const rolesState = useSelector(selectRoles);
  const roleList = rolesState?.roles || user?.roles || [];

  // NavListAuth normally starts the live session; pages without the header
  // need it too or the wait below would never end.
  useEffect(() => {
    if (user) startLiveSession();
  }, [user]);

  // "*" routes pass immediately — including public no-auth pages where no
  // user exists (forklift forms, external displays, logout).
  if (hasRouteAccess(roles, roleList, path, rolesState?.grantedPaths)) {
    return children;
  }

  // Auth still in flight; NavListAuth is about to redirect to /inituser.
  if (!user) return null;

  // This page load's grants haven't arrived yet — don't flash "no access"
  // at someone who was granted the page.
  if (!isSessionSynced()) return null;

  return (
    <div className="flex flex-col items-center px-4 py-24 text-center text-[color:var(--app-text)]">
      <span className="app-icon-tile mb-5 h-16 w-16 rounded-3xl bg-amber-500/15 text-amber-600 dark:text-amber-300">
        <LockRoundedIcon fontSize="large" />
      </span>
      <h1 className="text-2xl font-bold tracking-tight">
        You don't have access to this page
      </h1>
      <p className="app-muted mt-2 max-w-md">
        If you think you should, contact HelpDesk to have your roles updated.
      </p>
      <Link to="/" className="app-btn app-btn-primary mt-6">
        Back to home
      </Link>
    </div>
  );
}

function App({ pca }) {
  // The next 3 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  pca.setNavigationClient(navigationClient);

  return (
    <MsalProvider instance={pca}>
      <Pages />
    </MsalProvider>
  );
}

function Pages() {
  const authRequest = {
    ...loginRequest,
  };

  const [mfaVerify, setVerify] = useState(false);
  const [prevAuth, setPrevAuth] = useState(false);
  const user = useSelector(selectUser);

  return (
    <>
      <Routes>
        {routes.map((route, index) => {
          if (!route.requiresMFA) {
            return (
              <Route
                path={route.path}
                key={route.path}
                element={
                  route.authHeader ? (
                    <MsalAuthenticationTemplate
                      interactionType={InteractionType.Redirect}
                      authenticationRequest={authRequest}
                    >
                      <Helmet>
                        <title>{route.name}</title>
                      </Helmet>
                      {/* No `dark:contrast-150` here: a filter on this wrapper
                          creates a containing block that breaks fixed-position
                          descendants (modals, drawers), and now that dark mode
                          is toggle-driven it would hit every user, not just
                          those whose OS was already dark. Contrast is handled
                          by the palette instead. */}
                      <div
                        className={"flex flex-col h-screen justify-between"}
                      >
                        {route.authHeader && <NavListAuth />}
                        <main className="app-route-enter mb-auto">
                          <RequireRoles roles={route.roles} path={route.path}>{route.component}</RequireRoles>
                        </main>
                        {route.footer && <Footer />}
                      </div>
                    </MsalAuthenticationTemplate>
                  ) : (
                    <>
                      <Helmet>
                        <title>{route.name}</title>
                      </Helmet>
                      <RequireRoles roles={route.roles} path={route.path}>{route.component}</RequireRoles>
                    </>
                  )
                }
              />
            );
          } else {
            return (
              <Route
                path={route.path}
                key={route.path}
                element={
                  route.authHeader ? (
                    <MsalAuthenticationTemplate
                      interactionType={InteractionType.Redirect}
                      authenticationRequest={authRequest}
                    >
                      <Helmet>
                        <title>{route.name}</title>
                      </Helmet>
                      {mfaVerify || prevAuth ? (
                        <div className="flex flex-col h-screen justify-between">
                          {route.authHeader && <NavListAuth />}
                          <main className="app-route-enter mb-auto">
                          <RequireRoles roles={route.roles} path={route.path}>{route.component}</RequireRoles>
                        </main>
                          {route.footer && <Footer />}
                        </div>
                      ) : (
                        <div className="flex flex-col h-screen justify-between">
                          {route.authHeader && <NavListAuth />}
                          <MfaHandler
                            setPrevAuth={setPrevAuth}
                            setVerify={setVerify}
                            mfaVerify={mfaVerify}
                            prevAuth={prevAuth}
                          />
                          {route.footer && <Footer />}
                        </div>
                      )}
                    </MsalAuthenticationTemplate>
                  ) : (
                    <>
                      <Helmet>
                        <title>{route.name}</title>
                      </Helmet>
                      <RequireRoles roles={route.roles} path={route.path}>{route.component}</RequireRoles>
                    </>
                  )
                }
              />
            );
          }
        })}
      </Routes>
      {user && <RemoteRedirect />}
    </>
  );
}

export default App;
